import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import "../modal/ModalInput.css";

let openFn = null;
let closeFn = null;
let pendingConfig = null;

/* ========================= */
/* Public API                */
/* ========================= */

export function openInputField(config) {
  if (openFn) {
    openFn(config);
  } else {
    pendingConfig = config;
  }
}

export function closeInputField() {
  // Soft close (no Unity callback, just hides)
  closeFn && closeFn(false);
}

export function forceCloseInputField() {
  pendingConfig = null;
  closeFn && closeFn(true);
}

/* ========================= */
/* Modal Component           */
/* ========================= */

export const ModalInputWrapper = ({ sendMessage }) => {
  const [visible, setVisible] = useState(false);
  const [config, setConfig] = useState({});
  const [value, setValue] = useState("");
  const [offsetY, setOffsetY] = useState(0);

  const inputRef = useRef(null);

  // Detect iOS
  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

  // Focus input when modal opens
  useEffect(() => {
    if (!visible || !inputRef.current) return;

    const el = inputRef.current;

    // Multiple frames for iOS keyboard reliability
    const timeoutId = setTimeout(() => {
      el.focus({ preventScroll: true });

      // Move cursor to end for prefilled text
      const val = el.value;
      el.value = "";
      el.value = val;
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [visible]);

  /* ===== Keyboard-safe positioning ===== */
  useEffect(() => {
    if (!visible || !window.visualViewport) return;

    const updatePosition = () => {
      const vv = window.visualViewport;
      const keyboardHeight = window.innerHeight - vv.height - vv.offsetTop;
      setOffsetY(keyboardHeight > 0 ? -keyboardHeight / 2 : 0);
    };

    window.visualViewport.addEventListener("resize", updatePosition);
    window.visualViewport.addEventListener("scroll", updatePosition);
    updatePosition();

    return () => {
      window.visualViewport.removeEventListener("resize", updatePosition);
      window.visualViewport.removeEventListener("scroll", updatePosition);
    };
  }, [visible]);

  /* ===== Body scroll lock ===== */
  useEffect(() => {
    if (!visible) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  /* ===== Open / Close lifecycle ===== */
  useEffect(() => {
    openFn = (cfg) => {
      setConfig(cfg);
      setValue(cfg.value ?? cfg.initialValue ?? "");
      setVisible(true);
    };

    closeFn = () => setVisible(false);

    if (pendingConfig) {
      openFn(pendingConfig);
      pendingConfig = null;
    }

    return () => {
      openFn = null;
      closeFn = null;
    };
  }, []);

  /* ===== Focus + cursor placement ===== */
  useEffect(() => {
    if (!visible || !inputRef.current) return;

    requestAnimationFrame(() => {
      const el = inputRef.current;
      el.focus();

      if (config.selectOnFocus === "all") el.select();
      else {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    });
  }, [visible, config.selectOnFocus]);

  /* ========================= */
  /* Actions                  */
  /* ========================= */

  const isInvalid = !config.allowEmpty && !value.trim();

  const submit = () => {
    if (isInvalid) return;
    if (navigator.vibrate) navigator.vibrate(10);

    sendMessage(
      "UnityToWeb",
      "OnHtmlInputResult",
      JSON.stringify({ cancelled: false, value })
    );

    setVisible(false);
  };

  const cancel = () => {
    if (navigator.vibrate) navigator.vibrate(10);

    sendMessage(
      "UnityToWeb",
      "OnHtmlInputResult",
      JSON.stringify({ cancelled: true, value: "" })
    );

    setVisible(false);
  };

  if (!visible) return null;

  const isMultiline = config.multiline === true;
  const maxLength = config.maxLength ?? undefined;

  /* ========================= */
  /* Render                   */
  /* ========================= */

  return ReactDOM.createPortal(
    <div
      className="html-input-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="html-input-title"
      onClick={() => config.dismissOnBackdrop && cancel()}
    >
      <div
        className="html-input-modal"
        style={{ transform: `translateY(${offsetY}px)` }}
        onClick={(e) => e.stopPropagation()}
      >
        {config.title && <h3 id="html-input-title">{config.title}</h3>}
        {config.message && <p>{config.message}</p>}

        {/* ===== Input / Textarea ===== */}
        {/* Hidden fake password input to trick iOS */}
        {isIOS && config.inputType !== "password" && (
          <input
            type="password"
            autoComplete="new-password"
            style={{
              position: "absolute",
              opacity: 0,
              height: 0,
              width: 0,
              border: "none",
              padding: 0,
              margin: 0,
              pointerEvents: "none",
            }}
          />
        )}

        {isMultiline ? (
          <textarea
            ref={inputRef}
            rows={config.rows || 4}
            maxLength={maxLength}
            placeholder={config.placeholder}
            value={value}
            aria-label={config.title || "Input field"}
            autoComplete="off"
            spellCheck={true}
            autoCorrect="on"
            autoCapitalize="sentences"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (config.submitOnEnter && e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
              if (e.key === "Escape") cancel();
            }}
          />
        ) : (
          <input
            ref={inputRef}
            type={isIOS && config.inputType === "number" ? "text" : config.inputType || "text"}
            inputMode={config.inputType === "number" ? "numeric" : undefined}
            maxLength={maxLength}
            placeholder={config.placeholder}
            value={value}
            aria-label={config.title || "Input field"}
            autoComplete="off"
            spellCheck={true}
            autoCorrect="on"
            autoCapitalize={config.forceUppercase ? "characters" : "sentences"}
            onChange={(e) =>
              setValue(config.forceUppercase ? e.target.value.toUpperCase() : e.target.value)
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
              if (e.key === "Escape") cancel();
            }}
          />
        )}



        {/* ===== Character Counter ===== */}
        {config.showCounter && maxLength && (
          <div className="char-counter">
            {value.length} / {maxLength}
          </div>
        )}

        {/* ===== Buttons ===== */}
        <div className="buttons">
          <button onClick={cancel}>Cancel</button>
          <button onClick={submit} disabled={isInvalid}>
            {config.submitting ? "Saving..." : "OK"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
